<template>
  <div :class="controlClasses" class="mds-form-control mds-date-input">
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div
      :style="innerStyles"
      class="inner"
      :class="{ 'is-focused': calendarVisible }"
    >
      <div class="control-row">
        <VInputMask
          :id="uid"
          :ref="uid"
          :disabled="disabled"
          :clearable="clearable"
          :placeholder="placeholder"
          :mask="Utils.date.dateMask"
          :value="maskValue"
          raw
          @focus="onFocus"
          @input="onTextInput"
        >
          <template v-if="hasIconSlot" v-slot:icon>
            <!-- @slot Слот иконки -->
            <slot name="icon" />
          </template>
        </VInputMask>

        <template v-if="isDisabledHintVisible">
          <button class="disable-hint-container" :id="`hint-${uid}`">
            <VIcon theme="outlined" material="question_mark" />
          </button>

          <VTooltip :target="`#hint-${uid}`">
            <ul>
              <li v-for="(reason, index) in disabledReasons" :key="index">
                {{ reason }}
              </li>
            </ul>
          </VTooltip>
        </template>

        <VIcon
          v-else
          class="calendar-icon"
          material="calendar_today"
          @click="onClickIcon"
        />
      </div>

      <div class="messages-row" v-if="isErrorsVisible">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>

    <VPopup
      @click.stop
      class="mds-date-input-popup"
      :target="$refs[uid]"
      v-model:visible="calendarVisible"
    >
      <div class="mds-date-input__calendar">
        <VMonthRenderer
          v-for="(m, index) of monthCount"
          :key="uid + '_' + index"
          :first-day-of-week="1"
          :is-first="index === 0"
          :is-last="index + 1 === showMonths"
          :is-selected="isSelected"
          :is-disabled="isDisabled"
          :month="dataForMonthRender[index].month"
          :year="dataForMonthRender[index].year"
          @change-month="onChangeMonth"
          @select-day="onSelectDay"
          @wheel.prevent="onMonthScroll"
        />
      </div>
      <div class="popup-helper-text" v-if="hasPopupHelperTextSlot">
        <!-- @slot Слот вспомогательного текста для попап календаря -->
        <slot name="popup-helper-text" />
      </div>
    </VPopup>
  </div>
</template>

<style lang="scss" src="./VDateInput.scss" />

<script lang="ts">
import Utils        from "@/core/utils/Utils";
import DateSimple   from '@/core/types/DateSimple';
import TimeStamp    from '@/core/types/TimeStamp';
import VBaseControl from "@/core/services/VBaseControl";

import VIcon          from "@/lib/components/VIcon/VIcon.vue";
import VPopup         from "@/lib/components/VPopup/VPopup.vue";
import VTooltip       from "@/lib/components/VTooltip/VTooltip.vue";
import VInputMask     from "@/lib/components/VInputMask/VInputMask.vue";
import VMonthRenderer from "@/lib/components/VMonthRenderer/VMonthRenderer.vue";

export default defineComponent({
  name: "VDateInput",

  components: { VTooltip, VInputMask, VPopup, VMonthRenderer, VIcon },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** Иконка */
    icon: {
      type:    String,
      default: null,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: false,
    },
    /** v-model */
    modelValue: {
      type:    [Date, String, TimeStamp, null],
      default: null,
    },
    /** Обратная совместимость с :value */
    value: {
      type:    [Date, String, TimeStamp, null],
      default: null,
    },
    /** Количество месяцев в попапе */
    showMonths: {
      type:    Number,
      default: 2,
    },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => this.$core.constants.TEXT_ALIGNS.includes(value),
    },
  },

  emits: ['update:modelValue', 'input'],

  data() {
    return {
      Utils,
      selectedDate:    null,
      visibleDate:    null,
      maskValue:       null,
      calendarVisible: false,
    };
  },

  computed: {
    /** Приоритет у modelValue, затем value. */
    actualValue() {
      if (this.modelValue !== null) return this.modelValue;
      return this.value;
    },

    firstDayOfWeek(): number {
      return 1;
    },

    monthCount() {
      return new Array(this.showMonths).fill(null);
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true) classes.push("clearable");
      if (this.icon !== null)      classes.push("with-icon");
      return classes;
    },

    inputDate() {
      return Utils.date.format(this.selectedDate);
    },

    showClear(): boolean {
      return this.clearable === true
        && this.selectedDate !== null
        && this.disabled === false;
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    hasPopupHelperTextSlot(): boolean {
      return this.$slots["popup-helper-text"] !== undefined;
    },

    dataForMonthRender() {
      return new Array(this.showMonths).fill(null).map((item, index) => {
        let month = this.visibleDate.getMonth() + index;
        let year  = this.visibleDate.getFullYear();
        if (month > 11) {
          month = 0;
          year += 1;
        }
        return { month, year };
      });
    },

    hasValidationMethod(): boolean {
      return this.validateMethod !== null;
    },
  },

  watch: {
    actualValue: {
      handler(newVal) {
        if (newVal instanceof Date) {
          this.selectedDate = newVal;
        } else if (typeof newVal === "string" && isNaN(Date.parse(newVal)) === false) {
          this.selectedDate = new Date(newVal);
        } else {
          this.selectedDate = null;
        }
      },
      immediate: true,
    },

    selectedDate: {
      handler(newVal) {
        this.validate();
        if (newVal instanceof Date) {
          this.maskValue   = Utils.date.format(newVal);
          this.visibleDate = Utils.date.cloneDate(this.selectedDate);
        } else {
          this.visibleDate = new Date();
          this.maskValue   = null;
        }
      },
      immediate: true,
    },
  },

  created() {
    if (this.value instanceof Date) {
      this.selectedDate = this.value;
    } else if (typeof this.value === "string" && isNaN(Date.parse(this.value)) === false) {
      this.selectedDate = new Date(this.value);
      this.$emit("input", new DateSimple(this.selectedDate));
    }
    this.maskValue = this.inputDate;

    if (this.selectedDate !== null) {
      this.visibleDate = Utils.date.cloneDate(this.selectedDate);
    } else {
      this.visibleDate = new Date();
    }
  },

  methods: {
    onClickIcon(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.$refs[this.uid]) {
        this.$refs[this.uid].$el.querySelector("input").focus();
      }
    },

    toggleCalendar(value) {
      if (value === true) {
        window.addEventListener("keydown", this.keyDownListeners);
      } else {
        window.removeEventListener("keydown", this.keyDownListeners);
      }
      this.calendarVisible = value;
    },

    onChangeMonth(data) {
      const date = Utils.date.cloneDate(this.visibleDate);
      if (data.direction === "next") {
        date.setMonth(date.getMonth() + 1);
      } else {
        date.setMonth(date.getMonth() - 1);
      }
      this.visibleDate = date;
    },

    onMonthScroll(event) {
      const delta = event.deltaY || event.detail || event.wheelDelta;
      this.onChangeMonth({ direction: delta < 0 ? "previous" : "next" });
    },

    onSelectDay(date) {
      this.validation.markDirty();
      this.selectedDate = date;
      this.$emit("input", new DateSimple(this.selectedDate));
      this.$emit("update:modelValue", new DateSimple(this.selectedDate));
      this.toggleCalendar(false);
    },

    isSelected(date) {
      return Utils.date.format(this.selectedDate) === Utils.date.format(date);
    },

    isDisabled(date) {
      /**
       * Чтобы определить, валидна ли дата — проверяем что она вписывается
       * в рамки, без оглядки на диапазоны.
       */
      if (this.hasValidationMethod === true) return this.validateMethod(date);
      return null;
    },

    onTextInput(event) {
      const dateString = event.masked.length > 0 ? event.masked : null;
      this.maskValue = dateString;

      if (event.fullfilled === false) {
        this.selectedDate = null;
        this.$emit("input", this.selectedDate);
        return;
      }

      if (Utils.date.validInputString(dateString) === true) {
        this.selectedDate = new DateSimple(this.$core.utils.date.parseInputString(dateString));
      } else {
        this.selectedDate = null;
      }

      this.$emit("input", new DateSimple(this.selectedDate));
      this.$emit("update:modelValue", new DateSimple(this.selectedDate));

      if (this.selectedDate instanceof Date) {
        this.toggleCalendar(false);
      }
    },

    onFocus() {
      this.validation.markTouched();
      this.toggleCalendar(true);
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(Utils.date.cloneDate(this.selectedDate));
    },

    keyDownListeners(event) {
      if (event.key === "Escape" || event.key === "Tab") {
        const input = document.activeElement;
        if (input && typeof input.blur === "function") input.blur();
        this.toggleCalendar(false);
      }
    },
  },
});
</script>
